import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, Zap, BookOpen, Tv, ChevronRight, ArrowLeft, Crown, Sparkles, Bell, Check, FolderOpen } from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuthStore } from '../../store/authStore'

const MANGA_GENRES = [
  { label: 'Super Power', featured: true },
  { label: 'Action' },
  { label: 'Adventure' },
  { label: 'Isekai' },
  { label: 'Fantasy' },
  { label: 'Romance' },
  { label: 'Comedy' },
  { label: 'Drama' },
  { label: 'Mystery' },
  { label: 'Psychological' },
  { label: 'Sci-Fi' },
  { label: 'Horror' },
  { label: 'Thriller' },
  { label: 'Slice of Life' },
  { label: 'Historical' },
  { label: 'Martial Arts' },
  { label: 'Supernatural' },
  { label: 'Wuxia' },
]

const ANIME_GENRES = [
  { label: 'Super Power', featured: true },
  { label: 'Action' },
  { label: 'Adventure' },
  { label: 'Isekai' },
  { label: 'Fantasy' },
  { label: 'Supernatural' },
  { label: 'Comedy' },
  { label: 'Romance' },
  { label: 'Drama' },
  { label: 'Mystery' },
  { label: 'Sci-Fi' },
  { label: 'Horror' },
  { label: 'Slice of Life' },
  { label: 'Suspense' },
  { label: 'Sports' },
  { label: 'Mecha' },
  { label: 'Music' },
]

const STEPS = ['manga', 'anime', 'pro', 'done']

function GenreChip({ label, featured, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border transition-all ${
        selected
          ? 'bg-accent text-bg border-accent scale-105'
          : featured
            ? 'bg-accent/10 border-accent/40 text-accent hover:bg-accent/20'
            : 'bg-bg3 border-border text-gray-300 hover:border-accent/40 hover:text-white'
      }`}
    >
      {selected ? <Check size={11} /> : featured ? <Zap size={11} /> : null}
      {label}
    </button>
  )
}

export default function OnboardingModal({ onComplete, editMode = false, initialMangaGenres = [], initialAnimeGenres = [] }) {
  const { user, profile, session, setProfile } = useAuthStore()
  const [step, setStep]               = useState(0)
  const [mangaGenres, setMangaGenres] = useState(initialMangaGenres)
  const [animeGenres, setAnimeGenres] = useState(initialAnimeGenres)
  const [saving, setSaving]           = useState(false)

  const toggle = (list, setList, label) => {
    setList(prev => prev.includes(label) ? prev.filter(g => g !== label) : [...prev, label])
  }

  const save = async (skip = false) => {
    setSaving(true)
    const localUpdate = {
      onboarding_done: true,
      manga_genres: skip ? [] : mangaGenres,
      anime_genres: skip ? [] : animeGenres,
    }
    // Update profile locally first so the UI advances even if the network
    // call hangs (common in embedded browsers like WhatsApp/Instagram WebView).
    // The server write is best-effort — if it fails the user can re-edit
    // genres later from their profile.
    setProfile({ ...(profile || {}), ...localUpdate })

    // Race the supabase call against a hard timeout so a stuck request
    // can never freeze the modal. 5s is generous for a single-row update.
    const writeWithTimeout = Promise.race([
      supabase.from('profiles').update(localUpdate).eq('id', user.id).select().single(),
      new Promise(resolve => setTimeout(() => resolve({ data: null, error: 'timeout' }), 5000)),
    ])

    try {
      const { data } = await writeWithTimeout
      if (data) setProfile(data)
    } catch {
      // local update already applied above — nothing more to do
    } finally {
      setSaving(false)
      // Fire-and-forget welcome email — only on first onboarding, not editMode
      if (!editMode && session?.access_token) {
        fetch('/api/claude?action=send-welcome', {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        }).catch(() => {})
      }
      onComplete()
    }
  }

  const next = () => {
    // In editMode only steps 0 & 1 exist — save directly from step 1
    if (editMode && step === 1) { save(); return }
    if (step < STEPS.length - 2) setStep(s => s + 1)
    else save()
  }

  const back = () => setStep(s => s - 1)

  const totalSteps = editMode ? 2 : 3
  const progressWidth = `${Math.round(((step + 1) / totalSteps) * 100)}%`

  const currentMangaCount = mangaGenres.length
  const currentAnimeCount = animeGenres.length

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative bg-bg2 border border-border rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
      >
        {/* Progress bar */}
        <div className="h-1 bg-bg3">
          <motion.div
            className="h-full bg-accent"
            animate={{ width: progressWidth }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="p-6">
          {/* Logo + welcome */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-bg3 border border-accent/30 flex items-center justify-center flex-shrink-0">
              <span className="text-accent font-jp font-bold text-lg" style={{ textShadow: '0 0 10px rgba(56,189,248,0.5)' }}>仲</span>
            </div>
            <div>
              <p className="font-display tracking-widest gradient-text text-sm">NAKAMA</p>
              <p className="text-xs text-gray-500">
                {editMode ? 'Update your genre preferences' : "Let's personalise your experience"}
              </p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="manga"
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen size={18} className="text-accent" />
                  <h2 className="font-display text-xl">What manga do you read?</h2>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  {editMode ? 'Update your manga genre preferences.' : "Pick your favourite genres — we'll use these to personalise your feed."}
                  {currentMangaCount > 0 && <span className="ml-2 text-accent font-semibold">{currentMangaCount} selected</span>}
                </p>
                <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto pb-1">
                  {MANGA_GENRES.map(g => (
                    <GenreChip key={g.label} label={g.label} featured={g.featured}
                      selected={mangaGenres.includes(g.label)}
                      onClick={() => toggle(mangaGenres, setMangaGenres, g.label)} />
                  ))}
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="anime"
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <div className="flex items-center gap-2 mb-1">
                  <Tv size={18} className="text-accent" />
                  <h2 className="font-display text-xl">What anime do you watch?</h2>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  {currentMangaCount > 0 ? 'Great picks on manga! Now tell us your anime tastes.' : 'Tell us what anime genres you enjoy.'}
                  {currentAnimeCount > 0 && <span className="ml-2 text-accent font-semibold">{currentAnimeCount} selected</span>}
                </p>
                <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto pb-1">
                  {ANIME_GENRES.map(g => (
                    <GenreChip key={g.label} label={g.label} featured={g.featured}
                      selected={animeGenres.includes(g.label)}
                      onClick={() => toggle(animeGenres, setAnimeGenres, g.label)} />
                  ))}
                </div>
              </motion.div>
            )}
            {step === 2 && !editMode && (
              <motion.div key="pro"
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
                <div className="flex items-center gap-2 mb-1">
                  <Crown size={18} className="text-yellow-400" />
                  <h2 className="font-display text-xl">Unlock the full experience</h2>
                </div>
                <p className="text-sm text-gray-400 mb-4">A few features that make Nakama worth it.</p>
                <div className="space-y-2.5">
                  {[
                    { icon: Sparkles,    label: 'Unlimited AI search',   desc: 'Natural language — "dark isekai where MC is overpowered"' },
                    { icon: Crown,       label: 'AI Source Finder',      desc: 'Finds streams when nothing else works' },
                    { icon: Bell,        label: 'Chapter drop alerts',   desc: 'Push notifications when new chapters land' },
                    { icon: FolderOpen,  label: 'Reading Lists',         desc: 'Curate "Summer reads", "Best isekai" and share your taste' },
                  ].map(({ icon: Icon, label, desc }) => (
                    <div key={label} className="flex items-start gap-3 p-3 rounded-xl bg-bg3 border border-border">
                      <Icon size={15} className="text-accent mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-white">{label}</p>
                        <p className="text-xs text-gray-500">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            {/* Always-available skip route. Previously the Skip button only
                appeared on step 0; on later steps the user could only Back or
                continue, making it feel like a forced flow. Now there's an
                escape on every non-edit step. */}
            {editMode ? (
              <button onClick={onComplete} disabled={saving}
                className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors">
                Cancel
              </button>
            ) : (
              <div className="flex items-center gap-3">
                {step > 0 && (
                  <button onClick={back} disabled={saving}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors">
                    <ArrowLeft size={13} /> Back
                  </button>
                )}
                {step === 2 ? (
                  <button onClick={() => save(false)} disabled={saving}
                    className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors disabled:opacity-60">
                    {saving && <Loader2 size={11} className="animate-spin" />}
                    {saving ? 'Saving…' : 'Continue free'}
                  </button>
                ) : (
                  <button onClick={() => save(true)} disabled={saving}
                    className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
                    Skip for now
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              {/* Step dots */}
              <div className="flex gap-1.5">
                {(editMode ? [0, 1] : [0, 1, 2]).map(i => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === step ? 'bg-accent w-3' : 'bg-border'}`} />
                ))}
              </div>

              {step === 2 && !editMode ? (
                <Link
                  to="/upgrade"
                  onClick={() => save(false)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-yellow-500 text-bg font-bold rounded-xl text-sm hover:bg-yellow-400 transition-all"
                >
                  <Crown size={14} /> Get Pro
                </Link>
              ) : (
                <button onClick={next} disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-accent text-bg font-bold rounded-xl text-sm hover:bg-accent3 transition-all disabled:opacity-60">
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : step === 1 ? (
                    editMode ? <>Save <Zap size={14} /></> : <>Next <ChevronRight size={14} /></>
                  ) : (
                    <>Next <ChevronRight size={14} /></>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
